import { useMemo } from 'react';

import type { ImportTarget } from '../project-planning/excel-import/import-catalog';
import {
  PRODUCT_CODE_TARGET,
  PRODUCT_NAME_TARGET,
  PROJECT_CODE_TARGET,
  PROJECT_NAME_TARGET,
  WORK_ORDER_STATUS_TARGET,
  type ImportMapping,
  type ImportTransform,
  type NewWorkOrderStatus,
} from './transform';

const SELECT_CLASS =
  'border-input h-8 rounded-md border bg-transparent px-2 text-sm shadow-sm disabled:opacity-50';
const INPUT_CLASS =
  'border-input h-8 w-20 rounded-md border bg-transparent px-2 text-sm shadow-sm disabled:opacity-50';
const BLANK_INPUT_CLASS =
  'border-input h-8 w-44 rounded-md border bg-transparent px-2 text-sm shadow-sm disabled:opacity-50';

type TransformKind = ImportTransform['kind'];

/** Transform kinds offered per target, plus the "not mapped" sentinel. */
const KIND_OPTIONS: { value: TransformKind | ''; label: string }[] = [
  { value: '', label: '— Not mapped —' },
  { value: 'whole', label: 'Whole cell' },
  { value: 'first_n', label: 'First N characters' },
  { value: 'last_n', label: 'Last N characters' },
  { value: 'after_n', label: 'After N characters' },
  { value: 'concat', label: 'Combine columns' },
  { value: 'static', label: 'Static value' },
];

const NEEDS_N: ReadonlySet<TransformKind> = new Set(['first_n', 'last_n', 'after_n']);
const SINGLE_HEADER: ReadonlySet<TransformKind> = new Set([
  'whole',
  'first_n',
  'last_n',
  'after_n',
]);

/** Synthetic per-row project targets — resolved server-side, not part of the grid catalog. */
const PROJECT_TARGETS: ImportTarget[] = [
  { field: PROJECT_CODE_TARGET, label: 'Project Code', kind: 'text' },
  { field: PROJECT_NAME_TARGET, label: 'Project Name', kind: 'text' },
];

/**
 * Synthetic per-row product targets — resolved server-side against the
 * partner's products, not part of the grid catalog. Naming a product replaces
 * the container columns: the resolved product's own options are applied to the
 * work order exactly as if every container had been mapped.
 */
const PRODUCT_TARGET_ROWS: ImportTarget[] = [
  { field: PRODUCT_NAME_TARGET, label: 'Product', kind: 'text' },
  { field: PRODUCT_CODE_TARGET, label: 'Product Code', kind: 'text' },
];

const PRODUCT_TARGET_FIELDS: ReadonlySet<string> = new Set([
  PRODUCT_NAME_TARGET,
  PRODUCT_CODE_TARGET,
]);

/**
 * Synthetic first-row target for the status new work orders are created in.
 * Always rendered as a bespoke Draft/Scheduled row (never a source-column
 * mapping); persisted as a `static` transform on `WORK_ORDER_STATUS_TARGET`.
 */
const STATUS_TARGET: ImportTarget = {
  field: WORK_ORDER_STATUS_TARGET,
  label: 'New Work Order Status',
  kind: 'text',
};

const NEW_WORK_ORDER_STATUS_OPTIONS: { value: NewWorkOrderStatus; label: string }[] = [
  { value: 'draft', label: 'Draft' },
  { value: 'scheduled', label: 'Scheduled' },
];

interface ImportMappingEditorProps {
  /** Parsed spreadsheet headers (the available source columns). */
  headers: string[];
  /** Grid targets from `buildImportCatalog` (identity, containers, structured, metadata). */
  catalog: ImportTarget[];
  value: ImportMapping;
  onChange: (mapping: ImportMapping) => void;
  /** Disable all inputs (e.g. while an import is in flight). */
  disabled?: boolean;
  /**
   * Target fields whose row renders disabled while the rest stay editable — e.g.
   * the status row on non-Automatic imports, and the project code/name rows when
   * importing from within a project. Distinct from the whole-editor `disabled`.
   */
  lockedFields?: ReadonlySet<string>;
  /**
   * Target fields the import cannot run without — marked in the row label so
   * the requirement is visible where it is fixed, not only on the disabled
   * button. Used for Project Code when importing outside a project.
   */
  requiredFields?: ReadonlySet<string>;
  /**
   * Let the list flow with the page instead of scrolling in its own capped box.
   * Used by the full-page detail view; the modal dialog keeps the default
   * (constrained) so it can't grow unbounded.
   */
  flow?: boolean;
}

interface DraftParams {
  header: string;
  headers: string[];
  n: number;
  separator: string;
  /** Raw comma-split blank sentinels for a `whole` transform (normalised at apply). */
  blanks: string[];
  /** Constant applied by a `static` transform (no source column). */
  value: string;
}

function readDraft(entry: ImportMapping[number] | undefined): DraftParams {
  const t = entry?.transform;
  return {
    header: t && 'header' in t ? t.header : '',
    headers: t && t.kind === 'concat' ? t.headers : [],
    n: t && NEEDS_N.has(t.kind) ? (t as { n: number }).n : 1,
    separator: t && t.kind === 'concat' ? t.separator : '-',
    blanks: t && t.kind === 'whole' && t.blanks ? t.blanks : [],
    value: t && t.kind === 'static' ? t.value : '',
  };
}

function buildTransform(kind: TransformKind, d: DraftParams): ImportTransform {
  switch (kind) {
    case 'whole': {
      // Omit `blanks` unless the user entered a real sentinel, so untouched
      // `whole` mappings stay byte-identical to the pre-feature shape.
      const hasBlanks = d.blanks.some((b) => b.trim() !== '');
      return { kind: 'whole', header: d.header, ...(hasBlanks ? { blanks: d.blanks } : {}) };
    }
    case 'first_n':
      return { kind: 'first_n', header: d.header, n: d.n };
    case 'last_n':
      return { kind: 'last_n', header: d.header, n: d.n };
    case 'after_n':
      return { kind: 'after_n', header: d.header, n: d.n };
    case 'concat':
      return { kind: 'concat', headers: d.headers, separator: d.separator };
    case 'static':
      return { kind: 'static', value: d.value };
  }
}

/**
 * Transform-aware column mapping editor shared by the Excel-import wizard's map
 * step and the Imports detail page. For each grid target (plus the synthetic
 * `project.code` / `project.name` targets), the user picks a transform kind,
 * the source column(s), any `n` / separator, and — for a number_with_unit
 * target — the column's unit. Emits the `{ targetField, transform, unit? }[]`
 * mapping in catalog order.
 */
export function ImportMappingEditor({
  headers,
  catalog,
  value,
  onChange,
  disabled,
  lockedFields,
  requiredFields,
  flow,
}: ImportMappingEditorProps) {
  // Status row first, then the two product targets, then catalog targets, then
  // the two project targets, in a stable display order (also the order `emit`
  // rebuilds the mapping in). Product sits above the catalog because it
  // supersedes the container columns that follow it.
  const targets = useMemo(
    () => [STATUS_TARGET, ...PRODUCT_TARGET_ROWS, ...catalog, ...PROJECT_TARGETS],
    [catalog],
  );
  const byTarget = useMemo(() => new Map(value.map((e) => [e.targetField, e] as const)), [value]);

  // The mapped product target, if any. While one is set the resolved product
  // supplies every container option, so the container rows — and the other
  // product row — are disabled. The two are mutually exclusive, but a mapping
  // stored before that guard could carry both: prefer Product Code, matching
  // `validateImport`, so the row shown as active is the one the importer will
  // actually resolve against.
  const mappedProductField = [PRODUCT_CODE_TARGET, PRODUCT_NAME_TARGET].find((f) =>
    byTarget.has(f),
  );

  const containerFields = useMemo(
    () => new Set(catalog.filter((t) => t.kind === 'container').map((t) => t.field)),
    [catalog],
  );

  /**
   * Rebuild the whole mapping from a mutation to one target, in catalog order.
   * Container entries are stripped whenever a product target is mapped, so what
   * is saved matches what the importer will actually do (it ignores container
   * entries in that case) instead of leaving misleading dead rows behind.
   */
  function emit(next: Map<string, ImportMapping[number]>) {
    const productMapped = PRODUCT_TARGET_ROWS.some((t) => next.has(t.field));
    const mapping: ImportMapping = [];
    for (const target of targets) {
      if (productMapped && containerFields.has(target.field)) continue;
      const entry = next.get(target.field);
      if (entry) mapping.push(entry);
    }
    onChange(mapping);
  }

  function setKind(target: ImportTarget, kind: TransformKind | '') {
    const next = new Map(byTarget);
    if (kind === '') {
      next.delete(target.field);
      emit(next);
      return;
    }
    // Selecting one product target clears the other — only ever one product
    // column per mapping.
    if (PRODUCT_TARGET_FIELDS.has(target.field)) {
      for (const field of PRODUCT_TARGET_FIELDS) {
        if (field !== target.field) next.delete(field);
      }
    }
    const draft = readDraft(byTarget.get(target.field));
    // Seed a sensible source column when first mapping this target.
    if (SINGLE_HEADER.has(kind) && draft.header === '') draft.header = headers[0] ?? '';
    if (kind === 'concat' && draft.headers.length === 0 && draft.header) {
      draft.headers = [draft.header];
    }
    const existing = byTarget.get(target.field);
    // Seed the unit for a number_with_unit target so the emitted mapping is
    // explicit (mirrors the wizard's previous behaviour).
    const seededUnit =
      target.kind === 'number_with_unit'
        ? (existing?.unit ?? target.defaultUnit ?? target.units?.[0] ?? '')
        : existing?.unit;
    const entry: ImportMapping[number] = {
      targetField: target.field,
      transform: buildTransform(kind, draft),
      ...(seededUnit !== undefined ? { unit: seededUnit } : {}),
    };
    next.set(target.field, entry);
    emit(next);
  }

  function updateParams(target: ImportTarget, patch: Partial<DraftParams>) {
    const existing = byTarget.get(target.field);
    if (!existing) return;
    const draft = { ...readDraft(existing), ...patch };
    const next = new Map(byTarget);
    next.set(target.field, {
      targetField: target.field,
      transform: buildTransform(existing.transform.kind, draft),
      ...(existing.unit !== undefined ? { unit: existing.unit } : {}),
    });
    emit(next);
  }

  function setUnit(target: ImportTarget, unit: string) {
    const existing = byTarget.get(target.field);
    if (!existing) return;
    const next = new Map(byTarget);
    next.set(target.field, { ...existing, unit });
    emit(next);
  }

  /** Upsert the status row's `static` entry (Draft / Scheduled). */
  function setStatus(value: NewWorkOrderStatus) {
    const next = new Map(byTarget);
    next.set(WORK_ORDER_STATUS_TARGET, {
      targetField: WORK_ORDER_STATUS_TARGET,
      transform: { kind: 'static', value },
    });
    emit(next);
  }

  const statusEntry = byTarget.get(WORK_ORDER_STATUS_TARGET);
  const statusValue: NewWorkOrderStatus =
    statusEntry?.transform.kind === 'static' && statusEntry.transform.value === 'scheduled'
      ? 'scheduled'
      : 'draft';

  return (
    <div className={flow ? undefined : 'max-h-[55vh] overflow-auto'}>
      <table className="w-full text-sm">
        <thead
          className={
            flow
              ? 'text-muted-foreground text-left'
              : 'text-muted-foreground bg-background sticky top-0 text-left'
          }
        >
          <tr className="border-b">
            <th className="px-2 py-1.5 font-medium">Grid column</th>
            <th className="px-2 py-1.5 font-medium">Transform</th>
            <th className="px-2 py-1.5 font-medium">Source column(s)</th>
            <th className="px-2 py-1.5 font-medium">Options</th>
          </tr>
        </thead>
        <tbody>
          {targets.map((target) => {
            // A mapped product supersedes every container column, and the two
            // product columns supersede each other.
            const supersededByProduct =
              mappedProductField != null &&
              (target.kind === 'container' ||
                (PRODUCT_TARGET_FIELDS.has(target.field) && target.field !== mappedProductField));
            const rowDisabled =
              disabled || (lockedFields?.has(target.field) ?? false) || supersededByProduct;

            // The status row is bespoke: always a `static` Draft / Scheduled
            // value, never a source-column mapping.
            if (target.field === WORK_ORDER_STATUS_TARGET) {
              return (
                <tr key={target.field} className="border-b align-top">
                  <td className="px-2 py-1.5 font-medium">{target.label}</td>
                  <td className="text-muted-foreground px-2 py-1.5">Static value</td>
                  <td className="text-muted-foreground px-2 py-1.5">—</td>
                  <td className="px-2 py-1.5">
                    <label className="flex items-center gap-1 text-xs">
                      <span className="text-muted-foreground">Value</span>
                      <select
                        className={SELECT_CLASS}
                        value={statusValue}
                        disabled={rowDisabled}
                        aria-label="New work order status"
                        onChange={(e) => {
                          setStatus(e.target.value as NewWorkOrderStatus);
                        }}
                      >
                        {NEW_WORK_ORDER_STATUS_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </label>
                  </td>
                </tr>
              );
            }

            const entry = byTarget.get(target.field);
            const kind = entry?.transform.kind ?? '';
            const draft = readDraft(entry);
            const isNumberWithUnit = target.kind === 'number_with_unit';
            return (
              <tr key={target.field} className="border-b align-top last:border-0">
                <td className="px-2 py-1.5 font-medium">
                  {target.label}
                  {requiredFields?.has(target.field) && (
                    <span className="text-destructive ml-1" aria-hidden="true">
                      *
                    </span>
                  )}
                  {supersededByProduct ? (
                    <span className="text-muted-foreground block text-xs font-normal">
                      {target.kind === 'container'
                        ? 'Set by the mapped product'
                        : 'Only one product column can be mapped'}
                    </span>
                  ) : (
                    requiredFields?.has(target.field) && (
                      <span className="text-muted-foreground block text-xs font-normal">
                        Required
                      </span>
                    )
                  )}
                </td>
                <td className="px-2 py-1.5">
                  <select
                    className={SELECT_CLASS}
                    value={kind}
                    disabled={rowDisabled}
                    aria-label={`Transform for ${target.label}`}
                    onChange={(e) => {
                      setKind(target, e.target.value as TransformKind | '');
                    }}
                  >
                    {KIND_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-2 py-1.5">
                  {kind === '' || kind === 'static' ? (
                    <span className="text-muted-foreground">—</span>
                  ) : kind === 'concat' ? (
                    <div className="flex flex-col gap-0.5">
                      {headers.map((h) => (
                        <label key={h} className="flex items-center gap-1.5 text-xs">
                          <input
                            type="checkbox"
                            checked={draft.headers.includes(h)}
                            disabled={rowDisabled}
                            onChange={(e) => {
                              const set = new Set(draft.headers);
                              if (e.target.checked) set.add(h);
                              else set.delete(h);
                              // Preserve source order.
                              updateParams(target, {
                                headers: headers.filter((x) => set.has(x)),
                              });
                            }}
                          />
                          <span>{h}</span>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <select
                      className={SELECT_CLASS}
                      value={draft.header}
                      disabled={rowDisabled}
                      aria-label={`Source column for ${target.label}`}
                      onChange={(e) => {
                        updateParams(target, { header: e.target.value });
                      }}
                    >
                      {headers.map((h) => (
                        <option key={h} value={h}>
                          {h}
                        </option>
                      ))}
                    </select>
                  )}
                </td>
                <td className="px-2 py-1.5">
                  <div className="flex flex-wrap items-center gap-2">
                    {NEEDS_N.has(kind as TransformKind) && (
                      <label className="flex items-center gap-1 text-xs">
                        <span className="text-muted-foreground">N</span>
                        <input
                          type="number"
                          min={kind === 'after_n' ? 0 : 1}
                          value={draft.n}
                          disabled={rowDisabled}
                          aria-label={`N for ${target.label}`}
                          className={INPUT_CLASS}
                          onChange={(e) => {
                            const n = Number.parseInt(e.target.value, 10);
                            updateParams(target, { n: Number.isNaN(n) ? 0 : n });
                          }}
                        />
                      </label>
                    )}
                    {kind === 'concat' && (
                      <label className="flex items-center gap-1 text-xs">
                        <span className="text-muted-foreground">Separator</span>
                        <input
                          type="text"
                          value={draft.separator}
                          disabled={rowDisabled}
                          aria-label={`Separator for ${target.label}`}
                          className={INPUT_CLASS}
                          onChange={(e) => {
                            updateParams(target, { separator: e.target.value });
                          }}
                        />
                      </label>
                    )}
                    {kind === 'static' && (
                      <label className="flex items-center gap-1 text-xs">
                        <span className="text-muted-foreground">Value</span>
                        <input
                          type="text"
                          value={draft.value}
                          disabled={rowDisabled}
                          aria-label={`Static value for ${target.label}`}
                          className={BLANK_INPUT_CLASS}
                          onChange={(e) => {
                            updateParams(target, { value: e.target.value });
                          }}
                        />
                      </label>
                    )}
                    {kind !== '' && isNumberWithUnit && (target.units?.length ?? 0) > 0 && (
                      <label className="flex items-center gap-1 text-xs">
                        <span className="text-muted-foreground">Unit</span>
                        <select
                          className={SELECT_CLASS}
                          value={entry?.unit ?? target.defaultUnit ?? target.units?.[0] ?? ''}
                          disabled={rowDisabled}
                          aria-label={`Unit for ${target.label}`}
                          onChange={(e) => {
                            setUnit(target, e.target.value);
                          }}
                        >
                          {target.units!.map((u) => (
                            <option key={u} value={u}>
                              {u}
                            </option>
                          ))}
                        </select>
                      </label>
                    )}
                    {kind === 'whole' && (
                      <label className="flex items-center gap-1 text-xs">
                        <span className="text-muted-foreground">Blank</span>
                        <input
                          type="text"
                          value={draft.blanks.join(',')}
                          disabled={rowDisabled}
                          placeholder="NONE, No"
                          aria-label={`Values treated as blank for ${target.label}`}
                          className={BLANK_INPUT_CLASS}
                          onChange={(e) => {
                            updateParams(target, { blanks: e.target.value.split(',') });
                          }}
                        />
                      </label>
                    )}
                    {kind !== 'whole' &&
                      kind !== 'concat' &&
                      kind !== 'static' &&
                      !NEEDS_N.has(kind as TransformKind) &&
                      !isNumberWithUnit && <span className="text-muted-foreground">—</span>}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
